import { standardId, standardName } from '../../utils/standardRef'

function newId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID()
  return `b-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

export const BLOCK_TYPES = [
  { type: 'text', label: 'Text', hint: 'A single line' },
  { type: 'paragraph', label: 'Paragraph', hint: 'A longer note' },
  { type: 'heading', label: 'Heading', hint: 'A section title' },
  { type: 'page', label: 'Page', hint: 'A nested page' },
  { type: 'item', label: 'Item', hint: 'A code section' },
  { type: 'guide', label: 'Guide', hint: 'An attached guide' },
  { type: 'topic', label: 'Topic', hint: 'A topic' },
  { type: 'image', label: 'Image', hint: 'Paste or attach a picture' },
  { type: 'pdf', label: 'PDF', hint: 'Opens in the viewer' },
  { type: 'sketch', label: 'Sketch', hint: 'Draw with a pen or stylus' },
]

export function emptyText() {
  return { id: newId(), type: 'text', text: '' }
}

export function emptyParagraph() {
  return { id: newId(), type: 'paragraph', text: '' }
}

export function emptyHeading() {
  return { id: newId(), type: 'heading', level: 1, text: '' }
}

export function emptySketch() {
  return { id: newId(), type: 'sketch', height: 280, strokes: [] }
}

export function itemIdOf(item) {
  return String(item?._id || item || '')
}

function snapshotItem(item) {
  if (!item || typeof item !== 'object' || !item._id) return null
  return {
    _id: item._id,
    label: item.label || 'Item',
    standard: (() => {
      const id = typeof item.standard === 'object' ? standardId(item.standard) : ''
      const name = standardName(item.standard)
      return id ? { _id: id, name } : name
    })(),
    pageStart: item.pageStart,
    pageEnd: item.pageEnd,
    level: item.level,
  }
}

function snapshotGuide(guide) {
  if (!guide || typeof guide !== 'object' || !guide._id) return null
  return {
    _id: guide._id,
    title: guide.title || guide.name || 'Guide',
    name: guide.name || '',
    kind: guide.kind || '',
    url: guide.url || '',
    fileId: guide.fileId || '',
    contentType: guide.contentType || '',
  }
}

function snapshotTopic(topic) {
  if (!topic || typeof topic !== 'object' || !topic._id) return null
  return { _id: topic._id, name: topic.name || 'Topic' }
}

const ANSWER_SOURCES = ['searchroom', 'global']

function snapshotAnswer(answer) {
  if (!answer || typeof answer !== 'object' || !answer.text) return null
  return {
    source: ANSWER_SOURCES.includes(answer.source) ? answer.source : '',
    sourceLabel: answer.sourceLabel || '',
    question: answer.question || '',
    text: answer.text,
    model: answer.model || '',
    items: (Array.isArray(answer.items) ? answer.items : []).map(snapshotItem).filter(Boolean),
  }
}

// A copied AI answer (Searchroom or Global AI Search). Read-only: it is never
// offered in the block menu, only added from the answer's "Add to note" action.
export function answerBlock(answer) {
  const snap = snapshotAnswer(answer)
  return snap ? { id: newId(), type: 'answer', answer: snap } : null
}

export function itemBlock(item) {
  const snap = snapshotItem(item)
  return snap ? { id: newId(), type: 'item', item: snap } : null
}

export function guideBlock(guide) {
  const snap = snapshotGuide(guide)
  return snap ? { id: newId(), type: 'guide', guide: snap } : null
}

export function topicBlock(topic) {
  const snap = snapshotTopic(topic)
  return snap ? { id: newId(), type: 'topic', topic: snap } : null
}

function fileSnap(file) {
  const fileId = file?.fileId || file?._id
  if (!fileId) return null
  return {
    fileId: String(fileId),
    name: file.name || 'File',
    contentType: file.contentType || '',
  }
}

export function imageBlock(file) {
  const snap = fileSnap(file)
  return snap ? { id: newId(), type: 'image', ...snap } : null
}

export function pdfBlock(file) {
  const snap = fileSnap(file)
  return snap ? { id: newId(), type: 'pdf', ...snap } : null
}

export function pageBlock(page) {
  const pageId = page?._id || page?.pageId
  if (!pageId) return null
  return {
    id: newId(),
    type: 'page',
    pageId: String(pageId),
    title: page.title || 'Untitled',
  }
}

function refId(block, key) {
  const value = block?.[key]
  if (!value) return ''
  return String(value._id || value)
}

function sketchHeight(value) {
  const height = Number(value)
  if (!Number.isFinite(height)) return 280
  return Math.min(1600, Math.max(160, Math.round(height)))
}

function sketchStrokes(strokes) {
  if (!Array.isArray(strokes)) return []
  return strokes
    .map((stroke) => {
      if (!stroke || typeof stroke !== 'object') return null
      const points = (Array.isArray(stroke.points) ? stroke.points : [])
        .map((point) => {
          const x = Number(point?.x)
          const y = Number(point?.y)
          if (!Number.isFinite(x) || !Number.isFinite(y)) return null
          return { x: Math.min(1, Math.max(0, x)), y }
        })
        .filter(Boolean)
        .slice(0, 8000)
      if (!points.length) return null
      const size = Number(stroke.size)
      const color = typeof stroke.color === 'string' && /^#[0-9a-fA-F]{6}$/.test(stroke.color) ? stroke.color : '#1c1c1c'
      return {
        tool: stroke.tool === 'eraser' ? 'eraser' : 'pen',
        color,
        size: Number.isFinite(size) ? Math.min(40, Math.max(0.5, size)) : 2.4,
        points,
      }
    })
    .filter(Boolean)
    .slice(0, 400)
}

// A new page has no blocks. Existing heading/text blocks are kept; anything
// unrecognized becomes a text line so older pages still open.
export function normalizeBlocks(blocks) {
  if (!Array.isArray(blocks) || blocks.length === 0) return []
  return blocks
    .map((block) => {
      if (!block || typeof block !== 'object') return null
      const id = block.id || newId()
      if (block.type === 'heading') {
        return { id, type: 'heading', level: block.level === 2 ? 2 : 1, text: block.text || '' }
      }
      if (block.type === 'paragraph') return { id, type: 'paragraph', text: block.text || '' }
      if (block.type === 'item') {
        const item = snapshotItem(block.item) || (block.itemId ? { _id: block.itemId, label: 'Item' } : null)
        return item ? { id, type: 'item', item } : null
      }
      if (block.type === 'guide') {
        const guide = snapshotGuide(block.guide)
        return guide ? { id, type: 'guide', guide } : null
      }
      if (block.type === 'topic') {
        const topic = snapshotTopic(block.topic)
        return topic ? { id, type: 'topic', topic } : null
      }
      if (block.type === 'page') {
        const pageId = block.pageId || block.page?._id
        if (!pageId) return null
        return { id, type: 'page', pageId: String(pageId), title: block.title || 'Untitled' }
      }
      if (block.type === 'image' || block.type === 'pdf') {
        const file = fileSnap(block)
        return file ? { id, type: block.type, ...file } : null
      }
      if (block.type === 'sketch') {
        return { id, type: 'sketch', height: sketchHeight(block.height), strokes: sketchStrokes(block.strokes) }
      }
      if (block.type === 'answer') {
        const answer = snapshotAnswer(block.answer)
        return answer ? { id, type: 'answer', answer } : null
      }
      if (block.type === 'text' || block.text != null) {
        return { id, type: 'text', text: block.text || '' }
      }
      return null
    })
    .filter(Boolean)
}

// Pages saved before blocks carried attachments keep items/guides/topics on the
// page itself. Fold those in once so they show up as blocks.
export function mergeLegacyAttachments(blocks, page) {
  const next = [...normalizeBlocks(blocks)]
  const seen = {
    item: new Set(next.filter((b) => b.type === 'item').map((b) => refId(b, 'item'))),
    guide: new Set(next.filter((b) => b.type === 'guide').map((b) => refId(b, 'guide'))),
    topic: new Set(next.filter((b) => b.type === 'topic').map((b) => refId(b, 'topic'))),
  }
  const before = next.length
  ;(page?.items || []).forEach((item) => {
    const id = itemIdOf(item)
    if (!id || seen.item.has(id)) return
    const block = itemBlock(item)
    if (block) {
      next.push(block)
      seen.item.add(id)
    }
  })
  ;(page?.guides || []).forEach((guide) => {
    const id = itemIdOf(guide)
    if (!id || seen.guide.has(id)) return
    const block = guideBlock(guide)
    if (block) {
      next.push(block)
      seen.guide.add(id)
    }
  })
  ;(page?.topics || []).forEach((topic) => {
    const id = itemIdOf(topic)
    if (!id || seen.topic.has(id)) return
    const block = topicBlock(topic)
    if (block) {
      next.push(block)
      seen.topic.add(id)
    }
  })
  return { blocks: next, migrated: next.length !== before }
}

export function serializeBlocks(blocks) {
  return normalizeBlocks(blocks)
}

export function attachmentIds(blocks) {
  const items = []
  const guides = []
  const topics = []
  const push = (list, id) => {
    if (id && !list.includes(id)) list.push(id)
  }
  normalizeBlocks(blocks).forEach((block) => {
    if (block.type === 'item') push(items, refId(block, 'item'))
    if (block.type === 'guide') push(guides, refId(block, 'guide'))
    if (block.type === 'topic') push(topics, refId(block, 'topic'))
  })
  return { items, guides, topics }
}

export function moveBlock(blocks, fromId, toId) {
  if (!fromId || !toId || fromId === toId) return blocks
  const from = blocks.findIndex((block) => block.id === fromId)
  const to = blocks.findIndex((block) => block.id === toId)
  if (from < 0 || to < 0) return blocks
  const next = blocks.slice()
  const [moved] = next.splice(from, 1)
  next.splice(to, 0, moved)
  return next
}

export function insertAfter(blocks, afterId, block) {
  if (!block) return blocks
  const index = blocks.findIndex((entry) => entry.id === afterId)
  if (index < 0) return [...blocks, block]
  const next = blocks.slice()
  next.splice(index + 1, 0, block)
  return next
}

// Slash command text: the whole block is "/" plus an optional filter, no newline.
export function slashQuery(text) {
  if (typeof text !== 'string' || !text.startsWith('/')) return null
  if (text.includes('\n')) return null
  return text.slice(1).trim().toLowerCase()
}

export function filterBlockTypes(query) {
  const q = (query || '').trim().toLowerCase()
  if (!q) return BLOCK_TYPES
  return BLOCK_TYPES.filter(
    (entry) => entry.label.toLowerCase().includes(q) || entry.type.includes(q)
  )
}
